import { EXPERIENCE_PREVIEW_IDS } from '@/components/admin/experiences/preview/contracts';
import { ExperiencePreview } from '@/components/admin/experiences/preview/experience-preview';
import { previewPages } from '@/components/admin/experiences/preview/preview-walk';
import type { ReaderFormat } from '@/components/reader';
import { Button } from '@/components/ui/button';
import type { ExperienceDraft } from '@/lib/experience-draft';
import { DEFAULT_NOYAU_PREVIEW_PAIR, type NoyauPreviewPair } from '@/lib/noyau-preview';
import type { ExperienceVariable } from '@matter_extractor/shared';
import { Play } from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';

/**
 * La PORTE de l'aperçu plein écran (refonte modulaire §9.1, §9.3 ; issue #141).
 *
 * Un bouton, à côté de la bascule Téléphone | Écran, dans l'en-tête de l'éditeur
 * de page et dans l'onglet Timeline. Les deux écrans n'en savent rien d'autre :
 * tout ce que le calque a besoin de tenir - où l'on ouvre, ce qu'on a tapé,
 * l'endormissement de la chrome, le retour du focus - vit ici et dans le calque,
 * pas chez eux. C'est ce qui a permis de n'ajouter QUE cette ligne à deux
 * fichiers que d'autres lots touchent en même temps.
 *
 * ## Il ouvre sur la page COURANTE
 *
 * Celle qu'on édite, ou celle que le rail a sélectionnée. `←` remonte jusqu'au
 * début si on veut la marche entière, donc ouvrir au milieu ne perd rien et
 * ouvrir toujours au début aurait fait payer la traversée à chaque essai d'une
 * page tardive.
 *
 * ## Le focus revient ICI, et c'est pour ça que l'état est ici
 *
 * Un calque qui se ferme en laissant le focus sur `<body>` rend le clavier muet
 * jusqu'au prochain `Tab` depuis le haut de la page. Le bouton qui a ouvert est
 * le seul élément qui soit certain d'exister encore à la fermeture, donc c'est
 * lui qui garde la référence et se redonne le focus - le pendant du
 * `focusOnMount` que les boîtes et le tiroir de §4 posent à l'ouverture.
 *
 * ## Il est éteint quand il n'y a rien à jouer
 *
 * Une expérience dont aucune page n'est jouable - le cas que §9.5 décrit, une
 * page protocole vide et rien d'autre - n'a pas d'aperçu, et un calque ouvert
 * sur un écran vide serait une porte qui ne mène nulle part.
 */

export interface ExperiencePreviewButtonProps {
  readonly draft: ExperienceDraft;
  /** La page courante de l'écran qui porte le bouton. */
  readonly pageId: string | null;
  readonly format: ReaderFormat;
  /** Ce qu'une variable est NOMMÉE sur cet écran (D5) - l'écran tient déjà sa portée. */
  readonly variableNames: (variable: ExperienceVariable) => string;
  /**
   * « Aperçu avec » de l'éditeur de page (noyau.md N20), la paire que les blocs
   * Noyau montrent ; la Timeline n'en tient aucune et laisse le défaut, les deux
   * premiers noyaux de la table.
   */
  readonly noyauPair?: NoyauPreviewPair;
}

export function ExperiencePreviewButton({
  draft,
  pageId,
  format,
  variableNames,
  noyauPair = DEFAULT_NOYAU_PREVIEW_PAIR,
}: ExperiencePreviewButtonProps): ReactNode {
  const [open, setOpen] = useState(false);
  const door = useRef<HTMLButtonElement | null>(null);
  const playable = useMemo(() => previewPages(draft.pages).length, [draft.pages]);

  return (
    <>
      <Button
        ref={door}
        type="button"
        variant="outline"
        size="sm"
        data-testid={EXPERIENCE_PREVIEW_IDS.open}
        disabled={playable === 0}
        onClick={() => setOpen(true)}
      >
        <Play aria-hidden className="size-4" />
        Aperçu
      </Button>

      {open ? (
        <ExperiencePreview
          draft={draft}
          pageId={pageId}
          format={format}
          variableNames={variableNames}
          noyauPair={noyauPair}
          onClose={() => {
            setOpen(false);
            door.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}
